/**
 * PaymentDetailModal — view and manage a single payment (2026 redesign)
 *
 * - One-tap status chips (same square palette as the finance page)
 * - Compact info rows: patient / method / date / description
 * - Refund, cancel, reminder-task, SMS reminder, manual/auto invoice
 * - Inline expandable panels, bottom sheet on mobile
 *
 * Error Codes: CK-8850 - CK-8859
 */

import { useState, useMemo, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  selectClinicId,
  selectTherapistId,
  selectTherapistName,
  selectClinicProfile,
  selectCanCreateGreenInvoice,
  selectIsAdmin,
} from "../../store/slices/authSlice";
import {
  useUpdateTransactionStatusMutation,
  useUpdateTransactionMutation,
  useForgiveDebtMutation,
} from "../../store/services/financeApi";
import { useCreateTaskMutation } from "../../store/services/tasksApi";
// Grow payment links (Phase 5): chip in hero, info row, request action, manual-paid guard
import useGrowLinksMap from "../grow/useGrowLinksMap";
import GrowLinkStatusChip from "../grow/GrowLinkStatusChip";
import RequestPaymentButton from "../grow/RequestPaymentButton";
import useCancelGrowLinkOnManualPayment from "../../hooks/useCancelGrowLinkOnManualPayment";
import { useRefundGrowTransactionMutation } from "../../store/services/growApi";
import {
  TRANSACTION_STATUS,
  normalizeTransactionStatus,
  getTransactionStatusLabel,
  getPatient,
  getAppointment,
  sendPaymentReminderSMS,
  logActivity,
  GROW_ACTIVE_LINK_STATUSES,
} from "../../firebase";
import { formatHebrewDate, formatTime } from "../../utils/dateUtils";
import { useToast } from "../../contexts/ToastContext";
import {
  User,
  CalendarBlank,
  CurrencyCircleDollar,
  Clock,
  CheckCircle,
  Receipt,
  ArrowsClockwise,
  Warning,
  ArrowCounterClockwise,
  Bell,
  Trash,
  LinkSimple,
  FileText,
  Money,
  CreditCard,
  Bank,
  DeviceMobile,
  Hourglass,
  ArrowLeft,
  Check,
  CaretDown,
  PencilSimple,
  Ticket,
  PaperPlaneTilt,
} from "@phosphor-icons/react";
import LoaderLoop from "../shared/loader/LoaderLoop";
import ModalCloseButton from "../ui/ModalCloseButton";
import DatePicker from "../ui/DatePicker";
import "./PaymentDetailModal.css";

const PAYMENT_METHODS = {
  cash: { label: "מזומן", Icon: Money, color: "#22c55e" },
  credit: { label: "אשראי", Icon: CreditCard, color: "#3b82f6" },
  transfer: { label: "העברה", Icon: Bank, color: "#8b5cf6" },
  check: { label: "צ׳ק", Icon: FileText, color: "#f59e0b" },
  bit: { label: "אפליקציה", subLabel: "ביט/פייבוקס/אחר", Icon: DeviceMobile, color: "#ec4899" },
  punch_card: { label: "כרטיסיה", Icon: Ticket, color: "#14b8a6" },
  other: { label: "אחר", Icon: CurrencyCircleDollar, color: "#9ca3af" },
};

const STATUS_OPTIONS = [
  {
    value: TRANSACTION_STATUS.EXPECTED,
    label: "צפי הכנסה עתידי",
    icon: Hourglass,
    color: "expected",
  },
  {
    value: TRANSACTION_STATUS.PENDING,
    label: "ממתין לתשלום",
    icon: Clock,
    color: "pending",
  },
  {
    value: TRANSACTION_STATUS.PAID,
    label: "שולם",
    icon: CheckCircle,
    color: "paid",
  },
  {
    value: "invoiced",
    label: "הופקה חשבונית",
    icon: Receipt,
    color: "invoiced",
  },
];

function PaymentDetailModal({ transaction, isOpen, onClose, onUpdate, onInvoiceRequest }) {
  const navigate = useNavigate();
  const clinicId = useSelector(selectClinicId);
  const therapistId = useSelector(selectTherapistId);
  const therapistName = useSelector(selectTherapistName);
  const clinicProfile = useSelector(selectClinicProfile);
  const isUsingGreenInvoice = useSelector(selectCanCreateGreenInvoice);
  const { showSuccess, showError } = useToast();
  const [isSendingSmsReminder, setIsSendingSmsReminder] = useState(false);
  const [showStatusOptions, setShowStatusOptions] = useState(false);
  const statusSelectRef = useRef(null);
  // Grow
  const isAdmin = useSelector(selectIsAdmin);
  const growLinks = useGrowLinksMap(transaction ? [transaction] : []);
  const growLink = transaction ? growLinks.linkFor(transaction) : null;
  // Decision 2026-10-27: while a Grow payment request is active the transaction is locked for
  // manual status changes — the clinic cancels the request first (popover), then manages by hand.
  const growLocked = !!growLink && GROW_ACTIVE_LINK_STATUSES.includes(growLink.status);
  const growGuard = useCancelGrowLinkOnManualPayment();
  const [refundGrowTransaction] = useRefundGrowTransactionMutation();
  const [growRefundSum, setGrowRefundSum] = useState("");

  // Close the status dropdown on outside click
  useEffect(() => {
    if (!showStatusOptions) return;
    const handleClickOutside = (e) => {
      if (statusSelectRef.current && !statusSelectRef.current.contains(e.target)) {
        setShowStatusOptions(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, [showStatusOptions]);

  // Local status state - keeps the modal in sync after changes
  const [localStatus, setLocalStatus] = useState(null);

  useEffect(() => {
    if (transaction?.status) {
      setLocalStatus(transaction.status);
    }
  }, [transaction?.status]);

  useEffect(() => {
    if (transaction?.paymentMethod) {
      setLocalPaymentMethod(transaction.paymentMethod);
    }
  }, [transaction?.paymentMethod]);

  // Reset all local state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setShowStatusOptions(false);
      setShowRefundConfirm(false);
      setRefundReason("");
      setGrowRefundSum("");
      setShowReminderForm(false);
      setReminderNote("");
      setShowInvoiceChoice(false);
      setShowManualInvoiceForm(false);
      setManualInvoiceNumber("");
      setManualInvoiceDate("");
      setShowPaymentMethodPicker(false);
      setShowEditPaymentMethod(false);
      setShowCancelForecastConfirm(false);
      setLocalStatus(null);
      setLocalPaymentMethod(null);
    }
  }, [isOpen]);

  const [showRefundConfirm, setShowRefundConfirm] = useState(false);
  const [refundReason, setRefundReason] = useState("");
  const [showReminderForm, setShowReminderForm] = useState(false);
  const [reminderNote, setReminderNote] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [showInvoiceChoice, setShowInvoiceChoice] = useState(false);
  const [showManualInvoiceForm, setShowManualInvoiceForm] = useState(false);
  const [manualInvoiceNumber, setManualInvoiceNumber] = useState("");
  const [manualInvoiceDate, setManualInvoiceDate] = useState("");
  const [showPaymentMethodPicker, setShowPaymentMethodPicker] = useState(false);
  const [showEditPaymentMethod, setShowEditPaymentMethod] = useState(false);
  const [localPaymentMethod, setLocalPaymentMethod] = useState(null);
  const [showCancelForecastConfirm, setShowCancelForecastConfirm] = useState(false);

  const [updateTransactionStatus] = useUpdateTransactionStatusMutation();
  const [updateTransaction] = useUpdateTransactionMutation();
  const [forgiveDebt] = useForgiveDebtMutation();
  const [createTask] = useCreateTaskMutation();

  // Use localStatus for all status-derived values so changes reflect instantly
  const currentStatus = localStatus || transaction?.status;

  const normalizedStatus = useMemo(() => {
    return normalizeTransactionStatus(currentStatus);
  }, [currentStatus]);

  const statusLabel = useMemo(() => {
    return getTransactionStatusLabel(currentStatus);
  }, [currentStatus]);

  // Phase 10 — a transaction collected through a Grow link is refunded AT GROW (admin only).
  // Rules mirrored from the server: same-day = full only; max 2 refunds; remaining balance.
  const growRefund = useMemo(() => {
    if (!transaction || transaction.paidVia !== "grow" || !growLink || growLink.status !== "paid") return null;
    const toDate = (v) => (v?.toDate ? v.toDate() : v?.seconds ? new Date(v.seconds * 1000) : v ? new Date(v) : null);
    const refunds = Array.isArray(growLink.refunds) ? growLink.refunds : [];
    const refundedTotal = refunds.reduce((a, r) => a + (Number(r?.sum) || 0), 0);
    const remaining = Math.max(0, Math.round(((Number(growLink.sum) || 0) - refundedTotal) * 100) / 100);
    const txRefunded = (Array.isArray(transaction.growRefunds) ? transaction.growRefunds : []).reduce((a, r) => a + (Number(r?.sum) || 0), 0);
    const txRemaining = Math.max(0, Math.round(((Number(transaction.amount) || 0) - txRefunded) * 100) / 100);
    const paidAt = toDate(growLink.paidAt);
    const dayKey = (d) => (d && !Number.isNaN(d.getTime()) ? d.toLocaleDateString("en-CA", { timeZone: "Asia/Jerusalem" }) : null);
    const sameDay = !!paidAt && dayKey(paidAt) === dayKey(new Date());
    return {
      remaining,
      txRemaining,
      sameDay,
      refundsLeft: Math.max(0, 2 - refunds.length),
      multi: (growLink.settledTransactionIds || growLink.transactionIds || []).length > 1,
      defaultSum: sameDay ? remaining : Math.min(txRemaining, remaining),
    };
  }, [transaction, growLink]);

  const isRefundable = useMemo(() => {
    return (
      normalizedStatus === TRANSACTION_STATUS.PAID ||
      normalizedStatus === TRANSACTION_STATUS.INVOICE_MANUAL ||
      normalizedStatus === TRANSACTION_STATUS.INVOICE_AUTO
    );
  }, [normalizedStatus]);

  const isCancellable = useMemo(() => {
    return normalizedStatus === TRANSACTION_STATUS.PENDING;
  }, [normalizedStatus]);

  const isExpectedForecast = useMemo(() => {
    return normalizedStatus === TRANSACTION_STATUS.EXPECTED;
  }, [normalizedStatus]);

  const isCurrentlyInvoiced = useMemo(() => {
    return (
      normalizedStatus === TRANSACTION_STATUS.INVOICE_MANUAL ||
      normalizedStatus === TRANSACTION_STATUS.INVOICE_AUTO
    );
  }, [normalizedStatus]);

  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return "—";
    return `₪${parseFloat(amount).toLocaleString("he-IL")}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    const date = new Date(dateStr);
    return date.toLocaleDateString("he-IL", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  };

  const statusBucket = useMemo(() => {
    switch (normalizedStatus) {
      case TRANSACTION_STATUS.EXPECTED:
        return "expected";
      case TRANSACTION_STATUS.PENDING:
        return "pending";
      case TRANSACTION_STATUS.PAID:
        return "paid";
      case TRANSACTION_STATUS.INVOICE_MANUAL:
      case TRANSACTION_STATUS.INVOICE_AUTO:
        return "invoiced";
      case TRANSACTION_STATUS.SPLIT:
        return "split";
      case TRANSACTION_STATUS.REFUNDED:
        return "refunded";
      case TRANSACTION_STATUS.CANCELLED:
        return "cancelled";
      default:
        return "split";
    }
  }, [normalizedStatus]);

  const updateStatusDirectly = async (newStatus, additionalData = {}) => {
    // Grow: marking paid by hand while a payment link is active → ask first (never double-collect)
    if (newStatus === TRANSACTION_STATUS.PAID && transaction?.growLinkId) {
      const proceed = await growGuard.ensureNoActiveLink(transaction);
      if (!proceed) return;
    }
    setIsProcessing(true);

    try {
      await updateTransactionStatus({
        clinicId,
        transactionId: transaction.id,
        newStatus,
        additionalData,
        therapistId,
        therapistName,
        patientName: transaction.patientName,
        patientId: transaction.patientId,
      }).unwrap();

      setLocalStatus(newStatus);
      if (additionalData.paymentMethod) {
        setLocalPaymentMethod(additionalData.paymentMethod);
      }
      onUpdate?.();
    } catch (error) {
      console.error("[CK-8850] Error updating transaction status:", error);
      showError("שגיאה בעדכון סטטוס התשלום. נסו שוב.");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    if (!transaction) return;

    if (newStatus === "invoiced") {
      if (isCurrentlyInvoiced) return;
      setManualInvoiceNumber("");
      setManualInvoiceDate("");
      if (isUsingGreenInvoice) {
        setShowInvoiceChoice(true);
      } else {
        // Not connected: go straight to manual entry (optional number + date)
        setShowManualInvoiceForm(true);
      }
      return;
    }

    if (newStatus === normalizedStatus) return;

    // When changing to PAID, ask for payment method first
    if (newStatus === TRANSACTION_STATUS.PAID) {
      setShowPaymentMethodPicker(true);
      return;
    }

    await updateStatusDirectly(newStatus);
  };

  const handlePaymentMethodForPaid = async (method) => {
    setShowPaymentMethodPicker(false);
    await updateStatusDirectly(TRANSACTION_STATUS.PAID, {
      paymentMethod: method,
      actualPaymentDate: new Date().toISOString(),
    });
  };

  const handleEditPaymentMethod = async (method) => {
    if (!transaction) return;
    setIsProcessing(true);
    setShowEditPaymentMethod(false);

    try {
      await updateTransaction({
        clinicId,
        transactionId: transaction.id,
        updateData: { paymentMethod: method },
        therapistId,
        therapistName,
        patientName: transaction.patientName,
        patientId: transaction.patientId,
      }).unwrap();

      setLocalPaymentMethod(method);
      onUpdate?.();
    } catch (error) {
      console.error("[CK-8853] Error updating payment method:", error);
      showError("שגיאה בעדכון אמצעי התשלום.");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleInvoiceChoice = async (choice) => {
    if (!transaction) return;

    if (choice === "auto") {
      onInvoiceRequest?.(transaction);
      setShowInvoiceChoice(false);
      onClose();
    } else {
      // Manual: reveal optional invoice number + date entry
      setShowInvoiceChoice(false);
      setManualInvoiceNumber("");
      setManualInvoiceDate("");
      setShowManualInvoiceForm(true);
    }
  };

  // Note: `invoiceNumber`/`invoiceDate` are the API contract — firebase.js
  // updateTransactionStatus maps them to doc fields `manualInvoiceNumber`/`manualInvoiceDate`.
  const handleManualInvoiceConfirm = async () => {
    if (!transaction) return;
    const invoiceNumber = manualInvoiceNumber.trim();
    setShowManualInvoiceForm(false);
    await updateStatusDirectly(TRANSACTION_STATUS.INVOICE_MANUAL, {
      ...(invoiceNumber ? { invoiceNumber } : {}),
      ...(manualInvoiceDate ? { invoiceDate: manualInvoiceDate } : {}),
    });
  };

  const handleGrowRefund = async () => {
    if (!transaction || !growRefund) return;
    const sumNum = growRefund.sameDay ? growRefund.remaining : Number(growRefundSum === "" ? growRefund.defaultSum : growRefundSum);
    if (!Number.isFinite(sumNum) || sumNum <= 0) {
      showError("יש להזין סכום זיכוי תקין.");
      return;
    }
    if (sumNum > growRefund.remaining + 0.01) {
      showError(`סכום הזיכוי גבוה מהיתרה (${formatCurrency(growRefund.remaining)}).`);
      return;
    }
    setIsProcessing(true);
    const res = await refundGrowTransaction({
      clinicId,
      transactionId: transaction.id,
      refundSum: sumNum,
      reason: refundReason.trim() || null,
      linkId: growLink?.id,
      patientId: transaction.patientId,
    });
    setIsProcessing(false);
    if (res.error) {
      console.error("[CK-3877] Grow refund failed", { errorCode: "CK-3877", errorMessage: res.error.message, transactionId: transaction.id, timestamp: new Date().toISOString() });
      showError(res.error.message || "הזיכוי דרך Grow נכשל.");
      return;
    }
    const kind = res.data?.kind;
    showSuccess(kind === "partial" ? `זיכוי חלקי של ${formatCurrency(res.data?.refundSum)} בוצע דרך Grow` : `הזיכוי בוצע דרך Grow (${formatCurrency(res.data?.refundSum)})`);
    if (kind !== "partial") setLocalStatus(TRANSACTION_STATUS.REFUNDED);
    setShowRefundConfirm(false);
    setRefundReason("");
    setGrowRefundSum("");
    onUpdate?.();
    onClose();
  };

  const handleRefund = async () => {
    if (!transaction) return;
    if (growRefund) return handleGrowRefund();
    setIsProcessing(true);

    try {
      await updateTransactionStatus({
        clinicId,
        transactionId: transaction.id,
        newStatus: TRANSACTION_STATUS.REFUNDED,
        additionalData: { refundReason },
        therapistId,
        therapistName,
        patientName: transaction.patientName,
        patientId: transaction.patientId,
      }).unwrap();

      setShowRefundConfirm(false);
      setRefundReason("");
      onUpdate?.();
      onClose();
    } catch (error) {
      console.error("[CK-8850] Error refunding transaction:", error);
      showError("שגיאה בביצוע הזיכוי. נסו שוב.");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCancel = async () => {
    if (!transaction) return;
    if (transaction.growLinkId) {
      const proceed = await growGuard.ensureNoActiveLink(transaction, "cancel");
      if (!proceed) return;
    }
    setIsProcessing(true);

    try {
      await forgiveDebt({
        clinicId,
        transactionId: transaction.id,
        reason: "ביטול ידני",
        therapistId,
        therapistName,
        patientName: transaction.patientName,
        patientId: transaction.patientId,
        amount: transaction.amount,
      }).unwrap();

      onUpdate?.();
      onClose();
    } catch (error) {
      console.error("[CK-8851] Error cancelling transaction:", error);
      showError("שגיאה בביטול התשלום. נסו שוב.");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCancelForecast = async () => {
    if (!transaction) return;
    setIsProcessing(true);

    try {
      await forgiveDebt({
        clinicId,
        transactionId: transaction.id,
        reason: "ביטול צפי הכנסה עתידי",
        therapistId,
        therapistName,
        patientName: transaction.patientName,
        patientId: transaction.patientId,
        amount: transaction.amount,
      }).unwrap();

      setShowCancelForecastConfirm(false);
      onUpdate?.();
      onClose();
    } catch (error) {
      console.error("[CK-8856] Error cancelling expected forecast:", error);
      showError("שגיאה בביטול התחזית. נסו שוב.");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCreateReminder = async () => {
    if (!transaction || !reminderNote.trim()) return;
    setIsProcessing(true);

    try {
      const taskTitle = `תזכורת גבייה: ${transaction.patientName || "לקוח"} - ${formatCurrency(transaction.amount)}`;
      const taskDescription = `
סכום לגבייה: ${formatCurrency(transaction.amount)}
תאריך צפוי: ${formatDate(transaction.expectedPaymentDate || transaction.createdAt)}
${transaction.description ? `תיאור: ${transaction.description}` : ""}

הערה: ${reminderNote}
      `.trim();

      await createTask({
        clinicId,
        taskData: {
          title: taskTitle,
          description: taskDescription,
          priority: "high",
          dueDate: new Date().toISOString(),
          patientId: transaction.patientId || null,
          linkedTransactionId: transaction.id,
          scope: "personal",
        },
        creatorId: therapistId,
        creatorName: therapistName,
      }).unwrap();

      setShowReminderForm(false);
      setReminderNote("");
    } catch (error) {
      console.error("[CK-8852] Error creating reminder task:", error);
      showError("שגיאה ביצירת התזכורת.");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleGoToPatient = () => {
    if (transaction?.patientId) {
      navigate(`/dashboard/patients/${transaction.patientId}`);
      onClose();
    }
  };

  // Send an SMS reminder to the patient about this outstanding payment.
  const handleSendPaymentReminderSMS = async () => {
    if (!transaction || !clinicId || isSendingSmsReminder) return;
    setIsSendingSmsReminder(true);

    try {
      if (!transaction.patientId) {
        showError("לא ניתן לשלוח תזכורת לתשלום ללא שיוך למטופל.");
        return;
      }

      // Patient phone is not stored on the transaction - fetch it.
      const patient = await getPatient(clinicId, transaction.patientId);
      const phone = patient?.phone || patient?.mobilePhone || "";
      if (!phone) {
        showError(
          "לא נמצא מספר טלפון עבור מטופל זה. יש להוסיף מספר טלפון בעמוד המטופל."
        );
        return;
      }

      // Appointment date/time also not on the transaction - fetch appointment,
      // fall back to the transaction's expected payment date if none linked.
      let dateSource = transaction.expectedPaymentDate || transaction.createdAt;
      if (transaction.appointmentId) {
        const appointment = await getAppointment(
          clinicId,
          transaction.appointmentId
        );
        if (appointment?.startTime) dateSource = appointment.startTime;
      }
      const date = formatHebrewDate(dateSource, "dd/MM/yyyy");
      const time = formatTime(dateSource);

      const clinicName = clinicProfile?.name || null;

      const result = await sendPaymentReminderSMS({
        clinicId,
        transactionId: transaction.id,
        name: transaction.patientName || "לקוח",
        phone,
        clinicName,
        date,
        time,
      });

      if (result.success) {
        showSuccess("תזכורת תשלום נשלחה למטופל.");
      } else {
        showError(result.error || "שגיאה בשליחת תזכורת התשלום.");
      }

      logActivity(clinicId, {
        therapistId,
        therapistName,
        action: "send_payment_reminder_sms",
        entityType: "financial_transaction",
        entityId: transaction.id,
        entityName: transaction.patientName || "",
        success: !!result.success,
        errorMessage: result.success ? null : result.error || "",
      });
    } catch (error) {
      console.error("[CK-8854] Error sending payment reminder SMS:", {
        errorCode: "CK-8854",
        errorMessage: error.message,
      });
      showError("שגיאה בשליחת תזכורת התשלום.");
      logActivity(clinicId, {
        therapistId,
        therapistName,
        action: "send_payment_reminder_sms",
        entityType: "financial_transaction",
        entityId: transaction?.id,
        entityName: transaction?.patientName || "",
        success: false,
        errorMessage: error.message,
      });
    } finally {
      setIsSendingSmsReminder(false);
    }
  };

  const currentPaymentMethod = localPaymentMethod || transaction?.paymentMethod;
  const paymentMethodInfo =
    PAYMENT_METHODS[currentPaymentMethod] || PAYMENT_METHODS.other;
  const PaymentMethodIcon = paymentMethodInfo.Icon;

  const isPaid = normalizedStatus === TRANSACTION_STATUS.PAID ||
    normalizedStatus === TRANSACTION_STATUS.INVOICE_MANUAL ||
    normalizedStatus === TRANSACTION_STATUS.INVOICE_AUTO;

  if (!isOpen || !transaction) return null;

  // A panel replaces the actions area while open — one thing at a time
  const anyPanelOpen =
    showRefundConfirm ||
    showReminderForm ||
    showInvoiceChoice ||
    showManualInvoiceForm ||
    showPaymentMethodPicker ||
    showEditPaymentMethod ||
    showCancelForecastConfirm;

  const isTerminal =
    normalizedStatus === TRANSACTION_STATUS.REFUNDED ||
    normalizedStatus === TRANSACTION_STATUS.CANCELLED ||
    normalizedStatus === TRANSACTION_STATUS.SPLIT;

  return (
    <div className="pd2-overlay" onClick={onClose}>
      <div className="pd2-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="pd2-header">
          <div className="pd2-title">
            <div className={`pd2-title-icon pd2-tint-${statusBucket}`}>
              <CurrencyCircleDollar size={20} weight="fill" />
            </div>
            <div className="pd2-title-text">
              <span className="pd2-eyebrow">פרטי תשלום</span>
              <h3>{transaction.patientName || transaction.description || "הכנסה"}</h3>
            </div>
          </div>
          <ModalCloseButton onClick={onClose} />
        </div>

        <div className="pd2-body">
          {/* Amount + current status */}
          <div className="pd2-hero">
            <span className="pd2-amount">{formatCurrency(transaction.amount)}</span>
            <span className={`pd2-status-badge pd2-badge-${statusBucket}`}>
              {isProcessing ? (
                <LoaderLoop size="xs" />
              ) : null}
              {statusLabel}
            </span>
            {growLink && (
              <GrowLinkStatusChip link={growLink} onClick={() => growLinks.openLink(growLink)} />
            )}
          </div>

          {/* Grow lock — active payment request → no manual status change until it is cancelled */}
          {!isTerminal && growLocked && (
            <div className="pd2-status-select pd2-grow-locked">
              <span className="pd2-status-select-label">שינוי סטטוס תשלום</span>
              <div className="pd2-grow-locked-box">
                <LinkSimple size={16} weight="fill" />
                <div className="pd2-grow-locked-text">
                  <span>ממתין לתשלום דרך Grow — הסטטוס נעול לעדכון ידני.</span>
                  <small>לניהול ידני יש לבטל קודם את דרישת התשלום.</small>
                </div>
                <button type="button" className="pd2-grow-locked-btn" onClick={() => growLinks.openLink(growLink)}>
                  ביטול דרישת התשלום
                </button>
              </div>
            </div>
          )}

          {/* Status selector — full-width dropdown, deliberate action
              (hidden for terminal statuses and while a Grow request is active) */}
          {!isTerminal && !growLocked && (
            <div className="pd2-status-select" ref={statusSelectRef}>
              <span className="pd2-status-select-label">שינוי סטטוס תשלום</span>
              <button
                type="button"
                className={`pd2-status-trigger pd2-badge-${statusBucket} ${showStatusOptions ? "open" : ""}`}
                onClick={() => setShowStatusOptions((prev) => !prev)}
                disabled={isProcessing}
                aria-haspopup="listbox"
                aria-expanded={showStatusOptions}
              >
                <span className="pd2-status-trigger-value">
                  {isProcessing && <LoaderLoop size="xs" />}
                  {statusLabel}
                </span>
                <CaretDown
                  size={14}
                  weight="bold"
                  className={`pd2-status-caret ${showStatusOptions ? "open" : ""}`}
                />
              </button>

              {showStatusOptions && (
                <div className="pd2-status-menu" role="listbox">
                  {STATUS_OPTIONS.map((option) => {
                    const OptionIcon = option.icon;
                    const isActive = option.value === "invoiced"
                      ? isCurrentlyInvoiced
                      : normalizedStatus === option.value;
                    return (
                      <button
                        key={option.value}
                        type="button"
                        role="option"
                        aria-selected={isActive}
                        className={`pd2-status-option pd2-opt-${option.color} ${isActive ? "active" : ""}`}
                        onClick={() => {
                          setShowStatusOptions(false);
                          handleStatusChange(option.value);
                        }}
                        disabled={isActive || isProcessing}
                      >
                        <span className={`pd2-status-option-icon pd2-tint-${option.color}`}>
                          <OptionIcon size={14} weight="fill" />
                        </span>
                        <span className="pd2-status-option-label">{option.label}</span>
                        {isActive && <Check size={14} weight="bold" />}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Info rows */}
          <div className="pd2-info">
            {transaction.patientName ? (
              <button className="pd2-row clickable" onClick={handleGoToPatient}>
                <div className="pd2-row-icon">
                  <User size={15} weight="fill" />
                </div>
                <div className="pd2-row-text">
                  <span className="pd2-row-label">מטופל</span>
                  <span className="pd2-row-value">{transaction.patientName}</span>
                </div>
                <ArrowLeft size={13} weight="bold" className="pd2-row-affordance" />
              </button>
            ) : (
              <div className="pd2-row">
                <div className="pd2-row-icon">
                  <User size={15} weight="fill" />
                </div>
                <div className="pd2-row-text">
                  <span className="pd2-row-label">מטופל</span>
                  <span className="pd2-row-value muted">
                    <Warning size={12} /> ללא שיוך
                  </span>
                </div>
              </div>
            )}

            {isPaid ? (
              <button
                className="pd2-row clickable"
                onClick={() => setShowEditPaymentMethod(!showEditPaymentMethod)}
              >
                <div className="pd2-row-icon" style={{ color: paymentMethodInfo.color }}>
                  <PaymentMethodIcon size={15} weight="fill" />
                </div>
                <div className="pd2-row-text">
                  <span className="pd2-row-label">אמצעי תשלום</span>
                  <span className="pd2-row-value">
                    {paymentMethodInfo.label}
                    {paymentMethodInfo.subLabel && (
                      <span className="pd2-sub-label">{paymentMethodInfo.subLabel}</span>
                    )}
                  </span>
                </div>
                <PencilSimple size={13} weight="bold" className="pd2-row-affordance" />
              </button>
            ) : (
              <div className="pd2-row">
                <div className="pd2-row-icon" style={{ color: paymentMethodInfo.color }}>
                  <PaymentMethodIcon size={15} weight="fill" />
                </div>
                <div className="pd2-row-text">
                  <span className="pd2-row-label">אמצעי תשלום</span>
                  <span className="pd2-row-value">
                    {paymentMethodInfo.label}
                    {paymentMethodInfo.subLabel && (
                      <span className="pd2-sub-label">{paymentMethodInfo.subLabel}</span>
                    )}
                  </span>
                </div>
              </div>
            )}

            <div className="pd2-row">
              <div className="pd2-row-icon">
                <CalendarBlank size={15} weight="fill" />
              </div>
              <div className="pd2-row-text">
                <span className="pd2-row-label">
                  {normalizedStatus === TRANSACTION_STATUS.PENDING ? "תאריך צפוי" : "תאריך תשלום"}
                </span>
                <span className="pd2-row-value">
                  {formatDate(transaction.actualPaymentDate || transaction.expectedPaymentDate)}
                </span>
              </div>
            </div>

            {transaction.description && (
              <div className="pd2-row">
                <div className="pd2-row-icon">
                  <FileText size={15} weight="fill" />
                </div>
                <div className="pd2-row-text">
                  <span className="pd2-row-label">תיאור</span>
                  <span className="pd2-row-value">{transaction.description}</span>
                </div>
              </div>
            )}

            {growLink && (
              <button
                type="button"
                className="pd2-row clickable"
                onClick={() => growLinks.openLink(growLink)}
              >
                <div className="pd2-row-icon">
                  <LinkSimple size={15} weight="fill" />
                </div>
                <div className="pd2-row-text">
                  <span className="pd2-row-label">דרישת תשלום Grow</span>
                  <span className="pd2-row-value">
                    {growLink.status === "paid"
                      ? `שולם${growLink.payment?.methodLabel ? ` · ${growLink.payment.methodLabel}` : ""}${growLink.payment?.asmachta ? ` · אסמכתא ${growLink.payment.asmachta}` : ""}${growLink.refundStatus === "full" ? " · זוכה במלואו" : growLink.refundStatus === "partial" ? ` · זוכה ${formatCurrency(growLink.refundedTotal)}` : ""}`
                      : `${formatCurrency(growLink.sum)} · ${growLink.sentAt ? "נשלח למטופל" : "נוצר, טרם נשלח"}`}
                  </span>
                </div>
                <span className="pd2-row-affordance">פרטים</span>
              </button>
            )}
          </div>

          {/* Split Info Banner */}
          {transaction.splitFromTransactionId && (
            <div className="pd2-banner">
              <ArrowsClockwise size={14} weight="fill" />
              <span>רשומה זו נוצרה מפיצול תשלום</span>
            </div>
          )}

          {/* Invoice Links */}
          {transaction.RecepitsDocsRefArray && transaction.RecepitsDocsRefArray.length > 0 && (
            <div className="pd2-invoices">
              <span className="pd2-section-title">חשבוניות מקושרות</span>
              <div className="pd2-invoices-list">
                {transaction.RecepitsDocsRefArray.map((invoice, idx) => (
                  <a
                    key={idx}
                    href={invoice.invoiceUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="pd2-invoice-chip"
                  >
                    <Receipt size={13} weight="fill" />
                    <span>{invoice.invoiceNumber || `חשבונית ${idx + 1}`}</span>
                    <LinkSimple size={10} />
                  </a>
                ))}
              </div>
            </div>
          )}

          {/* Manual Invoice Details */}
          {normalizedStatus === TRANSACTION_STATUS.INVOICE_MANUAL &&
            (transaction.manualInvoiceNumber || transaction.manualInvoiceDate) && (
            <div className="pd2-banner invoiced">
              <Receipt size={14} weight="fill" />
              <span>
                חשבונית ידנית
                {transaction.manualInvoiceNumber && ` · מס׳ ${transaction.manualInvoiceNumber}`}
                {transaction.manualInvoiceDate && ` · ${formatDate(transaction.manualInvoiceDate)}`}
              </span>
            </div>
          )}

          {/* Actions — hidden while a panel is open */}
          {!anyPanelOpen && (
            <div className="pd2-actions">
              {(normalizedStatus === TRANSACTION_STATUS.PENDING || normalizedStatus === TRANSACTION_STATUS.EXPECTED) && !growLink && (
                <RequestPaymentButton
                  transactions={[transaction]}
                  size="md"
                  variant="secondary"
                  label="דרישת תשלום Grow"
                  className="pd2-grow-request"
                  onCreated={() => onUpdate?.()}
                />
              )}

              {normalizedStatus === TRANSACTION_STATUS.PENDING && (
                <button
                  className="pd2-action"
                  onClick={() => setShowReminderForm(true)}
                >
                  <Bell size={15} weight="fill" />
                  <span>תזכורת</span>
                </button>
              )}

              {normalizedStatus === TRANSACTION_STATUS.PENDING && (
                <button
                  className="pd2-action"
                  onClick={handleSendPaymentReminderSMS}
                  disabled={isSendingSmsReminder}
                >
                  {isSendingSmsReminder ? (
                    <LoaderLoop size="xs" />
                  ) : (
                    <PaperPlaneTilt size={15} weight="fill" />
                  )}
                  <span>תזכורת SMS</span>
                </button>
              )}

              {isCancellable && (
                <button
                  className="pd2-action danger"
                  onClick={handleCancel}
                  disabled={isProcessing}
                >
                  <Trash size={15} weight="fill" />
                  <span>ביטול</span>
                </button>
              )}

              {isExpectedForecast && (
                <button
                  className="pd2-action danger"
                  onClick={() => setShowCancelForecastConfirm(true)}
                  disabled={isProcessing}
                >
                  <Trash size={15} weight="fill" />
                  <span>ביטול צפי</span>
                </button>
              )}

              {isRefundable && (
                <button
                  className="pd2-action danger"
                  onClick={() => setShowRefundConfirm(true)}
                  disabled={!!growRefund && (!isAdmin || growRefund.refundsLeft === 0 || growRefund.remaining <= 0)}
                  title={
                    growRefund && !isAdmin
                      ? "זיכוי דרך Grow מתבצע על ידי מנהל/ת הקליניקה"
                      : growRefund && growRefund.refundsLeft === 0
                        ? "בוצעו 2 זיכויים — המקסימום של Grow"
                        : growRefund && growRefund.remaining <= 0
                          ? "העסקה כבר זוכתה במלואה"
                          : undefined
                  }
                >
                  <ArrowCounterClockwise size={15} weight="fill" />
                  <span>{growRefund ? "זיכוי דרך Grow" : "זיכוי"}</span>
                </button>
              )}
            </div>
          )}

          {/* Refund Confirmation */}
          {showRefundConfirm && (
            <div className="pd2-panel">
              <div className="pd2-panel-header">
                <ArrowCounterClockwise size={16} weight="fill" />
                <span>{growRefund ? "זיכוי דרך Grow" : "ביצוע זיכוי"}</span>
              </div>
              {growRefund && (
                <>
                  <p className="pd2-panel-text">
                    הכסף יוחזר למשלם מחשבון ה-Grow של הקליניקה.{" "}
                    {growRefund.sameDay ? (
                      <strong>ביום העסקה Grow מאפשרת ביטול מלא בלבד ({formatCurrency(growRefund.remaining)}).</strong>
                    ) : (
                      <>ניתן לזכות עד {formatCurrency(growRefund.remaining)}.</>
                    )}
                    {growRefund.multi && growRefund.sameDay ? " דרישת התשלום כוללת כמה תשלומים — כולם יסומנו כזוכו." : ""}
                    {" "}נותרו {growRefund.refundsLeft} זיכויים לעסקה זו. <strong>Grow אינה מפיקה חשבונית זיכוי</strong> — יש להפיק ידנית אם נדרש.
                  </p>
                  {!growRefund.sameDay && (
                    <label className="pd2-field">
                      <span>סכום הזיכוי (₪)</span>
                      <input
                        type="number"
                        inputMode="decimal"
                        dir="ltr"
                        min="0.01"
                        step="0.01"
                        max={growRefund.remaining}
                        value={growRefundSum === "" ? growRefund.defaultSum : growRefundSum}
                        onChange={(e) => setGrowRefundSum(e.target.value)}
                        className="pd2-input"
                        disabled={isProcessing}
                      />
                    </label>
                  )}
                </>
              )}
              <input
                type="text"
                value={refundReason}
                onChange={(e) => setRefundReason(e.target.value)}
                placeholder="סיבה (אופציונלי)"
                className="pd2-input"
                maxLength={200}
              />
              <div className="pd2-panel-actions">
                <button
                  className="pd2-btn secondary"
                  onClick={() => setShowRefundConfirm(false)}
                  disabled={isProcessing}
                >
                  ביטול
                </button>
                <button
                  className="pd2-btn danger"
                  onClick={handleRefund}
                  disabled={isProcessing}
                >
                  {isProcessing ? <LoaderLoop size="xs" /> : growRefund ? (growRefund.sameDay ? `ביטול עסקה · ${formatCurrency(growRefund.remaining)}` : "אשר זיכוי דרך Grow") : "אשר זיכוי"}
                </button>
              </div>
            </div>
          )}

          {/* Reminder Form */}
          {showReminderForm && (
            <div className="pd2-panel">
              <div className="pd2-panel-header">
                <Bell size={16} weight="fill" />
                <span>יצירת תזכורת</span>
              </div>
              <textarea
                value={reminderNote}
                onChange={(e) => setReminderNote(e.target.value)}
                placeholder="הערה לתזכורת..."
                rows={2}
                className="pd2-textarea"
              />
              <div className="pd2-panel-actions">
                <button
                  className="pd2-btn secondary"
                  onClick={() => setShowReminderForm(false)}
                  disabled={isProcessing}
                >
                  ביטול
                </button>
                <button
                  className="pd2-btn primary"
                  onClick={handleCreateReminder}
                  disabled={isProcessing || !reminderNote.trim()}
                >
                  {isProcessing ? <LoaderLoop size="xs" /> : "צור תזכורת"}
                </button>
              </div>
            </div>
          )}

          {/* Invoice Choice */}
          {showInvoiceChoice && (
            <div className="pd2-panel">
              <div className="pd2-panel-header">
                <Receipt size={16} weight="fill" />
                <span>הפקת חשבונית</span>
              </div>
              <p className="pd2-panel-text">
                איך תרצה להנפיק חשבונית עבור {transaction?.patientName || "התשלום"}?
              </p>
              <div className="pd2-option-grid">
                <button
                  className="pd2-option"
                  onClick={() => handleInvoiceChoice("auto")}
                  disabled={isProcessing}
                >
                  <Receipt size={20} weight="fill" />
                  <span>הפקה אוטומטית</span>
                  <small>דרך מורנינג</small>
                </button>
                <button
                  className="pd2-option"
                  onClick={() => handleInvoiceChoice("manual")}
                  disabled={isProcessing}
                >
                  <CheckCircle size={20} weight="fill" />
                  <span>סימון ידני</span>
                  <small>הנפקתי בעצמי</small>
                </button>
              </div>
              <button
                className="pd2-btn secondary full"
                onClick={() => setShowInvoiceChoice(false)}
                disabled={isProcessing}
              >
                ביטול
              </button>
            </div>
          )}

          {/* Manual Invoice Entry */}
          {showManualInvoiceForm && (
            <div className="pd2-panel">
              <div className="pd2-panel-header">
                <Receipt size={16} weight="fill" />
                <span>סימון חשבונית ידנית</span>
              </div>
              <p className="pd2-panel-text">פרטי החשבונית (לא חובה)</p>
              <label className="pd2-field">
                <span>מספר חשבונית</span>
                <input
                  type="text"
                  className="pd2-input"
                  placeholder="לדוגמה: 1042"
                  value={manualInvoiceNumber}
                  onChange={(e) => setManualInvoiceNumber(e.target.value)}
                />
              </label>
              <label className="pd2-field">
                <span>תאריך הפקת החשבונית</span>
                <DatePicker
                  value={manualInvoiceDate}
                  onChange={(e) => setManualInvoiceDate(e.target.value)}
                />
              </label>
              <div className="pd2-panel-actions">
                <button
                  className="pd2-btn secondary"
                  onClick={() => setShowManualInvoiceForm(false)}
                  disabled={isProcessing}
                >
                  ביטול
                </button>
                <button
                  className="pd2-btn primary"
                  onClick={handleManualInvoiceConfirm}
                  disabled={isProcessing}
                >
                  {isProcessing ? <LoaderLoop size="xs" /> : "שמירה"}
                </button>
              </div>
            </div>
          )}

          {/* Payment Method Picker - when marking as paid */}
          {showPaymentMethodPicker && (
            <div className="pd2-panel">
              <div className="pd2-panel-header">
                <CheckCircle size={16} weight="fill" />
                <span>סימון כשולם — בחר אמצעי תשלום</span>
              </div>
              <div className="pd2-method-grid">
                {Object.entries(PAYMENT_METHODS).map(([key, { label, Icon, color }]) => (
                  <button
                    key={key}
                    className="pd2-method"
                    style={{ "--method-color": color }}
                    onClick={() => handlePaymentMethodForPaid(key)}
                    disabled={isProcessing}
                  >
                    <Icon size={18} weight="fill" />
                    <span>{label}</span>
                  </button>
                ))}
              </div>
              <button
                className="pd2-btn secondary full"
                onClick={() => setShowPaymentMethodPicker(false)}
                disabled={isProcessing}
              >
                ביטול
              </button>
            </div>
          )}

          {/* Edit Payment Method */}
          {showEditPaymentMethod && (
            <div className="pd2-panel">
              <div className="pd2-panel-header">
                <PencilSimple size={16} weight="fill" />
                <span>שינוי אמצעי תשלום</span>
              </div>
              <div className="pd2-method-grid">
                {Object.entries(PAYMENT_METHODS).map(([key, { label, Icon, color }]) => {
                  const isSelected = currentPaymentMethod === key;
                  return (
                    <button
                      key={key}
                      className={`pd2-method ${isSelected ? "selected" : ""}`}
                      style={{ "--method-color": color }}
                      onClick={() => !isSelected && handleEditPaymentMethod(key)}
                      disabled={isProcessing || isSelected}
                    >
                      <Icon size={18} weight="fill" />
                      <span>{label}</span>
                      {isSelected && <Check size={13} weight="bold" />}
                    </button>
                  );
                })}
              </div>
              <button
                className="pd2-btn secondary full"
                onClick={() => setShowEditPaymentMethod(false)}
                disabled={isProcessing}
              >
                סגור
              </button>
            </div>
          )}

          {/* Cancel Forecast Confirmation */}
          {showCancelForecastConfirm && (
            <div className="pd2-panel">
              <div className="pd2-panel-header warning">
                <Warning size={16} weight="fill" />
                <span>ביטול צפי הכנסה עתידי</span>
              </div>
              {transaction.appointmentId ? (
                <>
                  <div className="pd2-banner">
                    <CalendarBlank size={14} weight="fill" />
                    <span>הפגישה עדיין קיימת ביומן</span>
                  </div>
                  <p className="pd2-panel-text">
                    ביטול הצפי הכספי <strong>לא יבטל את הפגישה עצמה</strong>.
                    לאחר שהפגישה תסתיים, תוכל/י עדיין לתעד את התשלום —
                    להעביר ל<strong>״ממתין לגבייה״</strong> או לסמן כ<strong>״שולם״</strong>.
                  </p>
                </>
              ) : (
                <p className="pd2-panel-text">
                  פעולה זו תבטל את רשומת הצפי הכספי. לא ניתן לבטל פעולה זו.
                </p>
              )}
              <div className="pd2-panel-actions">
                <button
                  className="pd2-btn secondary"
                  onClick={() => setShowCancelForecastConfirm(false)}
                  disabled={isProcessing}
                >
                  חזרה
                </button>
                <button
                  className="pd2-btn danger"
                  onClick={handleCancelForecast}
                  disabled={isProcessing}
                >
                  {isProcessing ? <LoaderLoop size="xs" /> : "אשר ביטול צפי"}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
      {growLinks.popover}
      {growGuard.prompt}
    </div>
  );
}

export default PaymentDetailModal;
